import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { obtenerSesion } from '../utils/sesion';
import ReporteCierre from '../components/ReporteCierre';
import ComprobanteCobro from '../components/ComprobanteCobro';
import { formatearMonto, formatearFechaHora } from '../utils/formato';
import { IconAdvertencia } from '../components/icons';
import '../styles/caja.css';

function Historial() {
  const sesion = obtenerSesion();
  const operador = sesion?.operador;

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [historial, setHistorial] = useState([]);
  const [abriendoId, setAbriendoId] = useState(null);
  const [detalle, setDetalle] = useState(null); // { sesion_caja, cobros, reporte_cierre }
  const [comprobante, setComprobante] = useState(null);

  useEffect(() => {
    async function cargar() {
      setCargando(true);
      setError('');
      try {
        const { data } = await api.get('/sesiones-caja/historial');
        setHistorial(data.historial);
      } catch (err) {
        setError('No se pudo cargar el historial. Reintentá en unos segundos.');
      } finally {
        setCargando(false);
      }
    }
    cargar();
  }, []);

  // Al abrir un cierre o un comprobante se vuelve al comienzo de la página (ver Caja.jsx).
  useEffect(() => {
    if (detalle || comprobante) window.scrollTo({ top: 0 });
  }, [detalle, comprobante]);

  // El detalle (con todos los movimientos del turno) se pide recién al abrir un cierre.
  async function abrirDetalle(id) {
    setError('');
    setAbriendoId(id);
    try {
      const { data } = await api.get(`/sesiones-caja/${id}`);
      setDetalle(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo abrir el detalle de ese cierre.');
    } finally {
      setAbriendoId(null);
    }
  }

  return (
    <div className="caja-container">
      <header className="caja-navbar no-imprimir">
        <div className="caja-navbar-marca">
          <img src="/logo-betech.svg" alt="Betech" className="caja-navbar-logo" />
          <div>
            <p className="caja-navbar-titulo">Historial de cierres</p>
            {operador && (
              <p className="caja-navbar-operador">
                {operador.usuario} · {operador.municipio_id}
              </p>
            )}
          </div>
        </div>
        <div className="caja-navbar-acciones">
          <Link to="/caja" className="btn-navbar">
            Volver a Caja
          </Link>
        </div>
      </header>

      <div className="caja-content">
        {error && (
          <p className="mensaje-error no-imprimir">
            <IconAdvertencia size={16} />
            {error}
          </p>
        )}

        {cargando && <p>Cargando…</p>}

        {!cargando && !detalle && (
          <section className="tarjeta">
            <h2>Cierres anteriores</h2>
            {historial.length === 0 ? (
              <p>Todavía no cerraste ninguna caja.</p>
            ) : (
              <div className="tabla-cobros-wrapper">
                <table className="tabla-cobros">
                  <thead>
                    <tr>
                      <th>Cierre</th>
                      <th>Monto inicial</th>
                      <th>Efectivo contado</th>
                      <th>Diferencia</th>
                      <th>Anulados</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map(({ sesion_caja, reporte_cierre }) => (
                      <tr key={sesion_caja.id}>
                        <td>{formatearFechaHora(sesion_caja.fecha_cierre)}</td>
                        <td>{formatearMonto(sesion_caja.monto_inicial)}</td>
                        <td>{formatearMonto(sesion_caja.monto_final_declarado)}</td>
                        <td className={Number(reporte_cierre.diferencia_arqueo) === 0 ? '' : 'diferencia-destacada'}>
                          {formatearMonto(reporte_cierre.diferencia_arqueo)}
                        </td>
                        <td>
                          {reporte_cierre.anulados?.cantidad > 0 ? (
                            <span className="badge-anulado">{reporte_cierre.anulados.cantidad}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="boton boton-secundario"
                            disabled={abriendoId === sesion_caja.id}
                            onClick={() => abrirDetalle(sesion_caja.id)}
                          >
                            {abriendoId === sesion_caja.id ? 'Abriendo…' : 'Ver detalle'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {detalle && comprobante && (
          <ComprobanteCobro
            cobro={comprobante}
            operador={operador}
            onVolver={() => setComprobante(null)}
            textoVolver="Volver al cierre"
          />
        )}

        {detalle && !comprobante && (
          <ReporteCierre
            sesionCaja={detalle.sesion_caja}
            reporteCierre={detalle.reporte_cierre}
            operador={operador}
            cobros={detalle.cobros}
            onVerComprobante={setComprobante}
            onVolver={() => setDetalle(null)}
            textoVolver="Volver al historial"
          />
        )}
      </div>
    </div>
  );
}

export default Historial;
